"use client";

import { useState } from "react";
import type { CatalogItem } from "@/lib/airtable";
import { usePieceContext } from "./PieceContext";

type Props = {
  item: Pick<CatalogItem, "id" | "itemName" | "pictureUrl" | "tagPrice" | "metal" | "stone" | "soldOut" | "size">;
  addToBagText?: string;
  soldOutText?: string;
  addedMessage?: string;
  alreadyInBagMessage?: string;
  cannotAddSoldOutMessage?: string;
  errorMessage?: string;
};

const BAG_STORAGE_KEY = "vault88-cart";

export function ItemActions({
  item,
  addToBagText = "Add to bag",
  soldOutText = "Sold out",
  addedMessage = "Added to bag",
  alreadyInBagMessage = "Already in bag",
  cannotAddSoldOutMessage = "Sold out items cannot be added to bag",
  errorMessage = "Unable to add to bag right now"
}: Props) {
  const [message, setMessage] = useState<string | null>(null);
  const { requestedSize } = usePieceContext();

  const handleAddToBag = () => {
    if (typeof window === "undefined") return;
    if (item.soldOut) {
      setMessage(cannotAddSoldOutMessage);
      return;
    }

    try {
      const existingRaw = window.localStorage.getItem(BAG_STORAGE_KEY);
      const existing = existingRaw ? (JSON.parse(existingRaw) as Array<{ id: string }>) : [];
      const alreadyExists = existing.some((entry) => entry.id === item.id);
      const next = alreadyExists
        ? existing
        : [
            ...existing,
            {
              id: item.id,
              itemName: item.itemName,
              pictureUrl: item.pictureUrl,
              tagPrice: item.tagPrice,
              metal: item.metal,
              stone: item.stone,
              size: item.size,
              ...(requestedSize ? { requestedSize } : {})
            }
          ];
      window.localStorage.setItem(BAG_STORAGE_KEY, JSON.stringify(next));
      setMessage(alreadyExists ? alreadyInBagMessage : addedMessage);
      window.dispatchEvent(new StorageEvent("storage", { key: BAG_STORAGE_KEY, newValue: JSON.stringify(next) }));
    } catch (error) {
      console.error("[Piece] Failed to add to bag", error);
      setMessage(errorMessage);
    }
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        className="w-full rounded-sm bg-[#c4a668] px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
        onClick={handleAddToBag}
        disabled={item.soldOut}
      >
        {item.soldOut ? soldOutText : addToBagText}
      </button>
      {message && <p className="text-center text-xs uppercase tracking-[0.3em] text-slate-500">{message}</p>}
    </div>
  );
}
